import type { FeedbackReaction } from "app/pages/sales-intelligence/modules/ai-agent/types/thread";
import createTrackingEventName from "app/pages/sales-intelligence/utils/testing/createTrackingEventName";
import type { track as TrackFunction } from "pro-features/components/WithTrack/src/WithTrack";
import type { CustomAttributes } from "app/services/track/track";

const createAIMeetingAgentTrackingService = (track: TrackFunction) => {
    const trackMeetingEvent = (action: string, eventName: string, customAttributes?: CustomAttributes): void => {
        track("meeting prep agent", action, eventName, customAttributes);
    };

    const trackSelectCompany = (company: string) => {
        const eventName = createTrackingEventName("select company", company);
        trackMeetingEvent("click", eventName);
    };

    const trackStartNewChat = () => {
        trackMeetingEvent("click", "start new chat");
    };

    const trackOpenHistory = () => {
        trackMeetingEvent("click", "history dropdown");
    };

    const trackOpenHistoryChat = (company: string) => {
        const eventName = createTrackingEventName("open previous chat", company);
        trackMeetingEvent("click", eventName);
    };

    const trackGenerateThread = (attendeesCount?: number) => {
        trackMeetingEvent("click", "generate brief", { attendeesCount: attendeesCount || 0 });
    };

    const trackPauseAgent = () => {
        trackMeetingEvent("click", "pause agent");
    };

    const trackSendMessage = (prompt: string) => {
        trackMeetingEvent("click", `send prompt//${prompt}`, { WordsCount: prompt.split(" ").length });
    };

    const trackInsertText = (textField: string) => {
        trackMeetingEvent("type", textField);
    };

    const trackAddCompetitor = (competitor: string) => {
        const eventName = createTrackingEventName("add competitor", competitor);
        trackMeetingEvent("click", eventName);
    };

    const trackRemoveCompetitor = (competitor: string) => {
        const eventName = createTrackingEventName("delete competitor", competitor);
        trackMeetingEvent("click", eventName);
    };

    const trackCopyBrief = (company: string) => {
        const eventName = createTrackingEventName("copy brief", company);
        trackMeetingEvent("click", eventName);
    };

    const trackSubmitFeedback = (company: string, reaction: FeedbackReaction) => {
        const eventName = createTrackingEventName(`${reaction} brief`, company);
        trackMeetingEvent("click", eventName);
    };

    const trackSelectMeetingGoal = (company: string, goal: string) => {
        const eventName = createTrackingEventName("select goal", goal, company);
        trackMeetingEvent("click", eventName);
    };

    const trackCopyText = (company: string) => {
        const eventName = createTrackingEventName("manual copy", company);
        trackMeetingEvent("click", eventName);
    };

    const trackAttendeesCheckboxSelect = (contactName: string, isSuggested: boolean) => {
        const subName = isSuggested ? "suggestions" : "search results";
        const eventName = createTrackingEventName("Attendee selection", subName, contactName);
        trackMeetingEvent("click", eventName);
    };

    const trackAttendeesCheckboxDeselect = () => {
        trackMeetingEvent("click", "Attendee deselect");
    };

    const trackAddNewAttendee = (userInput: string) => {
        const eventName = createTrackingEventName("Attendee selection", "add as new", userInput);
        trackMeetingEvent("click", eventName);
    };

    const trackBriefMessageToggle = (isCollapsed: boolean, sectionTitle: string) => {
        const eventName = isCollapsed ? "Expand single" : "Collapse single";
        trackMeetingEvent("click", `${eventName}/${sectionTitle}`);
    };

    const trackBriefMessageToggleAll = (eventName: string) => {
        trackMeetingEvent("click", eventName);
    };

    const trackDownloadBrief = () => {
        trackMeetingEvent("click", "Export PDF");
    };

    return {
        trackSelectCompany,
        trackStartNewChat,
        trackOpenHistory,
        trackOpenHistoryChat,
        trackGenerateThread,
        trackPauseAgent,
        trackSendMessage,
        trackInsertText,
        trackAddCompetitor,
        trackRemoveCompetitor,
        trackCopyBrief,
        trackSubmitFeedback,
        trackSelectMeetingGoal,
        trackCopyText,
        trackAttendeesCheckboxSelect,
        trackAttendeesCheckboxDeselect,
        trackAddNewAttendee,
        trackBriefMessageToggle,
        trackBriefMessageToggleAll,
        trackDownloadBrief,
    };
};

export default createAIMeetingAgentTrackingService;
